import React, { useState } from "react";
import axios from "axios";
import { Send, Sparkles, Loader2, Check, X, AlertTriangle, Copy } from "lucide-react";
import { Button } from "./components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_CTA = "Get the system from this video:";

/**
 * "Inject all videos" — opens a modal with the user's video list, lets them
 * pick a subset, runs the existing /api/videos/{id}/inject-link endpoint per
 * video sequentially (with a progress bar) and reports the result.
 *
 * Each row needs an existing short link — videos without one are flagged and
 * skipped (the user can click the per-row short-link button first).
 *
 * Props
 *  - videos        : array of dashboard video objects (from /api/videos)
 *  - ytStatus      : useYouTubeStatus() output
 *  - onReauthorize : reauth callback
 *  - utmSettings   : utm config (used to mint missing short links inline)
 */
export function BulkInjectButton({ videos, ytStatus, onReauthorize, utmSettings }) {
  const [open, setOpen] = useState(false);
  const [cta, setCta] = useState(DEFAULT_CTA);
  const [selected, setSelected] = useState({});
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState({});  // { [video_id]: 'pending' | 'ok' | error string }

  const canWrite = !!(ytStatus?.connected && ytStatus?.can_write);
  const writeFallbackMode = !canWrite && !!ytStatus?.connected;

  // Default-select all videos when opening
  const openDialog = (e) => {
    e?.stopPropagation();
    if (!ytStatus?.connected) { onReauthorize?.(); return; }
    if (!videos || videos.length === 0) {
      toast.error("No videos available to inject.");
      return;
    }
    const initial = {};
    videos.forEach((vm) => { initial[vm.video.video_id] = true; });
    setSelected(initial);
    setResults({});
    setOpen(true);
  };

  const toggle = (vid) => setSelected((s) => ({ ...s, [vid]: !s[vid] }));
  const selectAll = () => {
    const initial = {};
    videos.forEach((vm) => { initial[vm.video.video_id] = true; });
    setSelected(initial);
  };
  const clearAll = () => setSelected({});

  const ensureShortLink = async (v) => {
    // POST is idempotent — same (user, video_id) always returns the same slug
    const utm_content = v.title
      ? v.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60)
      : "";
    await axios.post(`${API}/links/short`, {
      video_id: v.video_id,
      video_title: v.title || "",
      base_url: utmSettings?.baseUrl || "https://yourwebsite.com",
      utm_source: utmSettings?.utmSource || "youtube",
      utm_medium: utmSettings?.utmMedium || "video",
      utm_content,
    });
  };

  const runBulk = async () => {
    const targets = videos.filter((vm) => selected[vm.video.video_id]);
    if (targets.length === 0) { toast.error("Select at least one video."); return; }

    // Fallback: legacy sessions that connected before we upgraded to
    // youtube.force-ssl get a combined block listing every short link for the
    // selected videos, copied to the clipboard. The user can then paste it
    // once, or per video, while being nudged to reconnect.
    if (writeFallbackMode) {
      setRunning(true);
      try {
        const lines = [];
        for (const vm of targets) {
          const v = vm.video;
          try {
            await ensureShortLink(v);
            const { data } = await axios.get(`${API}/videos/${v.video_id}/inject-block`, {
              params: { cta_line: cta || DEFAULT_CTA },
            });
            lines.push(`▶ ${v.title}\n${data.block_text}\n`);
            setResults((prev) => ({ ...prev, [v.video_id]: "ok" }));
          } catch (err) {
            const d = err?.response?.data?.detail;
            setResults((prev) => ({ ...prev, [v.video_id]: typeof d === "object" ? d?.message || d?.code : (d || "failed") }));
          }
        }
        const all = lines.join("\n────────────────────────────────\n\n");
        try {
          await navigator.clipboard.writeText(all);
        } catch {
          const ta = document.createElement("textarea");
          ta.value = all;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        toast.success(`Copied ${lines.length} block${lines.length === 1 ? "" : "s"} — paste into YouTube Studio.`);
      } finally {
        setRunning(false);
      }
      return;
    }

    setRunning(true);
    const next = {};
    targets.forEach((vm) => { next[vm.video.video_id] = "pending"; });
    setResults(next);

    let ok = 0;
    let fail = 0;

    for (const vm of targets) {
      const vid = vm.video.video_id;
      try {
        await ensureShortLink(vm.video);
        await axios.post(`${API}/videos/${vid}/inject-link`, {
          cta_line: cta || DEFAULT_CTA,
          dry_run: false,
        });
        setResults((prev) => ({ ...prev, [vid]: "ok" }));
        ok += 1;
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === "object" ? (detail?.message || detail?.code) : (detail || err.message || "failed");
        setResults((prev) => ({ ...prev, [vid]: msg }));
        fail += 1;
        // Hard-stop on reauth-required — no point continuing the queue
        if (typeof detail === "object" && (detail.code === "reauthorize_required" || detail.code === "youtube_not_connected")) {
          toast.error("YouTube authorization issue — stopping. Reconnect and try again.");
          break;
        }
      }
    }

    setRunning(false);
    if (fail === 0) toast.success(`Injected ${ok} video${ok === 1 ? "" : "s"}.`);
    else toast.error(`Injected ${ok}, failed ${fail}. See per-row status.`);
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <>
      <Button
        onClick={openDialog}
        size="sm"
        className={`gap-1.5 ${canWrite
          ? "bg-red-600 hover:bg-red-500 text-white border border-red-400/30"
          : writeFallbackMode
            ? "bg-red-600/80 hover:bg-red-500 text-white border border-red-400/30"
            : "bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30"}`}
        data-testid="bulk-inject-trigger"
        disabled={!videos || videos.length === 0}
        title={
          canWrite
            ? "Inject your short link into ALL video descriptions in one click"
            : writeFallbackMode
              ? "Copy a ready-to-paste block for every selected video to your clipboard (reconnect YouTube to enable one-click bulk push)"
              : "Connect YouTube to enable bulk inject"
        }
      >
        {canWrite || writeFallbackMode ? <Sparkles className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
        Inject all
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border border-white/10 text-white sm:max-w-2xl" data-testid="bulk-inject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-red-400" /> Inject link into multiple videos
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              Updates the description of each selected video on YouTube. Idempotent — re-running replaces the existing block. Same CTA + short link for every video.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {writeFallbackMode && (
              <div className="rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs px-3 py-2.5 flex gap-2" data-testid="bulk-pending-banner">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-400" />
                <div>
                  <strong className="text-amber-100">Reconnect to enable bulk push.</strong> Your YouTube connection was made before we upgraded to write access. Sign out of YouTube in the sidebar and reconnect (you&rsquo;ll get a &ldquo;Manage your YouTube account&rdquo; permission) — then this button will push blocks to every selected video automatically. For now, we&rsquo;ll copy them to your clipboard so you can paste them into each description in YouTube Studio.
                </div>
              </div>
            )}

            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-widest mb-1.5 block">Call-to-action (same for every video)</label>
              <Input
                value={cta}
                onChange={(e) => setCta(e.target.value)}
                placeholder={DEFAULT_CTA}
                className="bg-black/40 border-white/10 text-white"
                data-testid="bulk-cta-input"
                maxLength={140}
                disabled={running}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="text-xs text-zinc-400">
                {selectedCount} of {videos?.length || 0} selected
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={selectAll} disabled={running} className="h-7 text-xs text-zinc-300 hover:text-white" data-testid="bulk-select-all">All</Button>
                <Button size="sm" variant="ghost" onClick={clearAll} disabled={running} className="h-7 text-xs text-zinc-300 hover:text-white" data-testid="bulk-select-none">None</Button>
              </div>
            </div>

            <div className="border border-white/10 rounded-lg max-h-72 overflow-y-auto" data-testid="bulk-video-list">
              {(videos || []).map((vm) => {
                const v = vm.video;
                const status = results[v.video_id];
                return (
                  <label
                    key={v.video_id}
                    className="flex items-center gap-3 px-3 py-2 border-b border-white/[0.05] hover:bg-white/[0.03] cursor-pointer last:border-b-0"
                    data-testid={`bulk-row-${v.video_id}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!selected[v.video_id]}
                      onChange={() => toggle(v.video_id)}
                      disabled={running}
                      className="accent-red-500 w-4 h-4 flex-shrink-0"
                    />
                    <img src={v.thumbnail_url} alt="" className="w-12 h-7 rounded object-cover flex-shrink-0 border border-white/10" />
                    <div className="text-sm text-zinc-200 truncate flex-1 min-w-0">{v.title}</div>
                    <div className="flex-shrink-0">
                      {status === "pending" && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
                      {status === "ok" && <Check className="w-4 h-4 text-emerald-400" />}
                      {status && status !== "pending" && status !== "ok" && (
                        <span className="text-[11px] text-red-400 truncate max-w-[180px]" title={status}>
                          <X className="w-3 h-3 inline mr-0.5" /> {status.slice(0, 30)}
                        </span>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={running} className="border-white/10 hover:bg-white/5" data-testid="bulk-cancel">
              Close
            </Button>
            <Button onClick={runBulk} disabled={running || selectedCount === 0} className="bg-red-600 hover:bg-red-500 text-white" data-testid="bulk-run">
              {running ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> {writeFallbackMode ? "Copying…" : "Injecting…"}</>
              ) : writeFallbackMode ? (
                <><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy {selectedCount} block{selectedCount === 1 ? "" : "s"}</>
              ) : (
                <><Send className="w-3.5 h-3.5 mr-1.5" /> Inject {selectedCount} video{selectedCount === 1 ? "" : "s"}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
